import FrameworkUtils from 'utils/framework';
import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import Actions from 'actions/Actions';
import BrandsListActions from 'actions/BrandsListActions';
import { userSelector } from 'selectors/user/userSelector';
import BrandsListSelector from 'selectors/brandsList/brandsListSelector';
import TestTargetUtils from 'utils/TestTarget';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import BeautyPreferencesUtils from 'utils/BeautyPreferences';
import bpRedesignedUtils from 'utils/BeautyPreferencesRedesigned';
import userUtils from 'utils/User';
import BeautyPreferencesSelector from 'selectors/beautyPreferences/beautyPreferencesSelector';
import { HEADER_VALUE } from 'constants/authentication';
import BeautyPreferencesWorldActions from 'actions/BeautyPreferencesWorldActions';
import { customerPreferenceSelector } from 'selectors/user/customerPreferenceSelector';
import { WORLD_KEYS_ARRAY } from 'components/BeautyPreferencesWorld/constants';

const { beautyPreferencesSelector } = BeautyPreferencesSelector;
const { getBobFlag } = TestTargetUtils;
const { getBrandsByIdMap } = BeautyPreferencesUtils;
const { buildAllowedBrandsMaps, collectFavBrandIdsFromCustomerPreference, buildFavoriteBrandsRawData } = bpRedesignedUtils;
const { wrapHOC } = FrameworkUtils;
const { brandsListSelector } = BrandsListSelector;
const { getTextFromResource, getLocaleResourceFile } = LanguageLocaleUtils;
const { setUserFavoriteBrandIDs } = BrandsListActions;
const { showBiRegisterModal, showInterstice, showSignInModal } = Actions;
const getText = getLocaleResourceFile('components/Brand/BrandsList/locales', 'BrandsList');
const getTextFavoriteBrands = getLocaleResourceFile('components/Brand/BrandsList/FavoriteBrands/locales', 'FavoriteBrands');

const localization = createStructuredSelector({
    newText: getTextFromResource(getText, 'new'),
    allBrands: getTextFromResource(getText, 'allBrands'),
    brandsAZ: getTextFromResource(getText, 'brandsAZ'),
    favoriteBrands: getTextFromResource(getTextFavoriteBrands, 'favoriteBrands'),
    saveYourFBsMessageOne: getTextFromResource(getTextFavoriteBrands, 'saveYourFBsMessageOne'),
    beautyPreferences: getTextFromResource(getTextFavoriteBrands, 'beautyPreferences'),
    saveYourFBsMessageTwo: getTextFromResource(getTextFavoriteBrands, 'saveYourFBsMessageTwo'),
    favoriteBrandsAppearHere: getTextFromResource(getTextFavoriteBrands, 'favoriteBrandsAppearHere'),
    viewAllBautyPrefs: getTextFromResource(getTextFavoriteBrands, 'viewAllBautyPrefs')
});

const BRANDS = 'brands';

const fields = createSelector(
    userSelector,
    brandsListSelector,
    (_, ownProps) => ownProps.groupedBrands,
    localization,
    beautyPreferencesSelector,
    customerPreferenceSelector,
    (user, brandsList, groupedBrands, textResources, { beautyPreferences }, customerPreference) => {
        const brandsByIdMap = getBrandsByIdMap(groupedBrands);

        // Allowed brand maps for the whole list
        const { perWorld: allowedWorldBrandIds, flatMap: allowedBrandsMap } = buildAllowedBrandsMaps(WORLD_KEYS_ARRAY, BRANDS);

        // Hydrate favorites from customerPreference (across worlds), keep only those visible on the page and allowed
        const favsFromCP = collectFavBrandIdsFromCustomerPreference(customerPreference, WORLD_KEYS_ARRAY);
        const userFavoriteBrandIDs = favsFromCP.filter(id => {
            const inPage = brandsByIdMap[String(id)] || brandsByIdMap[Number(id)];

            return !!inPage && !!allowedBrandsMap[String(id)];
        });

        const userHasLovedBrands = !!userFavoriteBrandIDs.length;
        const totalUserLovedBrands = userHasLovedBrands ? ` (${userFavoriteBrandIDs.length})` : '';
        const isUserSignedIn = !!user.profileId;
        const isBIUser = userUtils.isBI();
        const isSignedIn = isUserSignedIn && isBIUser;
        const isNonBIUser = isUserSignedIn && !isBIUser;
        const bpPageHref = '/profile/BeautyPreferences?favoriteBrandsSpoke=true';

        return {
            bobFlag: getBobFlag(),
            userFavoriteBrandIDs,
            brandsByIdMap,
            allowedBrandsMap,
            allowedWorldBrandIds,
            localization: textResources,
            user,
            beautyPreferences,
            customerPreference,
            userHasLovedBrands,
            totalUserLovedBrands,
            isSignedIn,
            isNonBIUser,
            bpPageHref
        };
    }
);

const functions = dispatch => ({
    // This updates favorite brands for all worlds, but won't mess with other unrelated worlds.
    // The 'options' argument (optional, 3rd param) lets you pass things like:
    // - toggledBrandId: the brand the user just clicked
    // - remove: true if you're removing a favorite
    // - customerPreference: current customer preference data
    updateFavoriteBrands: (favBrands, done, options = {}) => {
        const { updateCustomerPreference } = BeautyPreferencesWorldActions;

        const rawData = buildFavoriteBrandsRawData({
            favBrands,
            toggledId: options?.toggledBrandId ? String(options.toggledBrandId) : '',
            remove: !!options?.remove,
            customerPreference: options?.customerPreference,
            worldKeys: WORLD_KEYS_ARRAY,
            refinementKey: BRANDS
        });

        dispatch(
            updateCustomerPreference(rawData, null, () => {
                // Update local slice so hearts and Favorite Brands re-render instantly
                dispatch(setUserFavoriteBrandIDs(favBrands));

                if (typeof done === 'function') {
                    try {
                        done(false);
                    } catch (error) {
                        Sephora.logger.verbose('Error hiding loader in updateFavoriteBrands callback:', error);
                    }
                }
            })
        ).catch(error => {
            Sephora.logger.verbose('updateCustomerPreference failed for favorite brands:', error);

            // If we have an error, we still update the local slice to remove the brand from the UI
            if (typeof done === 'function') {
                try {
                    done(false);
                } catch (e) {
                    Sephora.logger.verbose('Error hiding loader after failure:', e);
                }
            }
        });
    },
    showSignInModal: () => {
        dispatch(showSignInModal({ isOpen: true, extraParams: { headerValue: HEADER_VALUE.USER_CLICK } }));
    },
    showBiRegisterModal: () => {
        dispatch(showBiRegisterModal({ isOpen: true }));
    },
    showLoader: (show = false) => dispatch(showInterstice(show))
});

const withBrandsListProps = wrapHOC(connect(fields, functions));

export {
    fields, functions, localization, withBrandsListProps
};
